import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { ChartBar, Download, Calendar, Notebook, CurrencyDollar, CheckCircle, Clock } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

function Reports() {
  const [batches, setBatches] = useState([]);
  const [tutorBatchIds, setTutorBatchIds] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedBatch, setSelectedBatch] = useState('');
  const [reportType, setReportType] = useState('attendance');
  const [reportData, setReportData] = useState(null);
  const [batchSummary, setBatchSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchReport();
    fetchBatchSummary();
  }, [selectedMonth, selectedYear, selectedBatch, reportType]);

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
      const tutorIds = response.data.filter(b => b.name.toLowerCase().startsWith('tutor')).map(b => b.id);
      setTutorBatchIds(tutorIds);
    } catch (error) {
      toast.error('Failed to fetch batches');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const endpoint = reportType === 'attendance' 
        ? '/reports/attendance-monthly' 
        : '/reports/fees-monthly';
      
      const params = new URLSearchParams({
        month: selectedMonth.toString(),
        year: selectedYear.toString(),
      });
      if (selectedBatch && selectedBatch !== '') {
        params.append('batch_id', selectedBatch);
      }

      const response = await api.get(`${endpoint}?${params.toString()}`);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchSummary = async () => {
    try {
      const response = await api.get(
        `/reports/batch-summary?month=${selectedMonth}&year=${selectedYear}`
      );
      setBatchSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch batch summary');
    }
  };

  const handleExport = async () => {
    try {
      const endpoint = reportType === 'attendance'
        ? '/reports/export-attendance'
        : '/reports/export-fees';
      
      const params = new URLSearchParams({
        month: selectedMonth.toString(),
        year: selectedYear.toString(),
      });
      if (selectedBatch && selectedBatch !== '') {
        params.append('batch_id', selectedBatch);
      }

      const response = await api.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_${months[selectedMonth - 1]}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const getBatchName = (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.name : 'Unknown';
  };

  return (
    <Layout>
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-50 mb-2">
            Reports & Analytics
          </h1>
          <p className="text-base text-slate-400">Monthly attendance and fees reports</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
              <Calendar size={14} className="inline mr-1" weight="duotone" />
              Month
            </label>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger data-testid="month-select" className="bg-slate-950 border-slate-800 text-slate-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {months.map((month, idx) => (
                  <SelectItem key={idx} value={(idx + 1).toString()} className="text-slate-50">
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
              Year
            </label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger data-testid="year-select" className="bg-slate-950 border-slate-800 text-slate-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {[2024, 2025, 2026].map((year) => (
                  <SelectItem key={year} value={year.toString()} className="text-slate-50">
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
              <Notebook size={14} className="inline mr-1" weight="duotone" />
              Batch
            </label>
            <Select value={selectedBatch || "all"} onValueChange={(v) => setSelectedBatch(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="batch-select" className="bg-slate-950 border-slate-800 text-slate-50">
                <SelectValue placeholder="All Batches" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all" className="text-slate-50">All Batches</SelectItem>
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id} className="text-slate-50">
                    {batch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
              <ChartBar size={14} className="inline mr-1" weight="duotone" />
              Report Type
            </label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger data-testid="report-type-select" className="bg-slate-950 border-slate-800 text-slate-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="attendance" className="text-slate-50">Attendance</SelectItem>
                <SelectItem value="fees" className="text-slate-50">Fees</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {batchSummary && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-50 mb-4">Batch-wise Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {batchSummary.batches.map((batch) => {
                const isTutor = tutorBatchIds.includes(batch.batch_id);
                return (
                <div
                  key={batch.batch_id}
                  className="bg-slate-900 border border-slate-800 rounded-lg p-6 hover:border-slate-700 transition-colors"
                  data-testid={`batch-summary-${batch.batch_id}`}
                >
                  <h3 className="text-lg font-semibold text-slate-50 mb-4">{batch.batch_name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Students:</span>
                      <span className="font-medium text-slate-50">{batch.total_students}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Attendance Count:</span>
                      <span className="font-medium text-cyan-400">{batch.attendance_count}</span>
                    </div>
                    {!isTutor && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Fees Paid:</span>
                          <span className="font-medium text-emerald-400">{batch.fees_paid}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Fees Pending:</span>
                          <span className="font-medium text-amber-400">{batch.fees_pending}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-800">
                          <span className="text-slate-400">Total Collected:</span>
                          <span className="font-bold text-emerald-400">KWD {batch.total_fees_collected}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-50">
              {reportType === 'attendance' ? 'Attendance Report' : 'Fees Report'}
            </h2>
            <Button
              onClick={handleExport}
              data-testid="export-button"
              className="bg-blue-600 hover:bg-blue-500 flex items-center gap-2"
            >
              <Download size={18} weight="bold" />
              Export to Excel
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading report...</div>
          ) : reportData ? (
            <div className="overflow-x-auto">
              {reportType === 'attendance' ? (
                <table className="w-full">
                  <thead className="bg-slate-950 border-b border-slate-800">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">S.No</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Student Name</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Days Present</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Total Days</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {[...reportData.report]
                      .sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''))
                      .map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-800/50">
                        <td className="px-4 py-3 text-slate-400 font-medium">{idx + 1}</td>
                        <td className="px-4 py-3 text-slate-50 font-medium">{row.student_name}</td>
                        <td className="px-4 py-3 text-slate-400">{row.attendance_count}</td>
                        <td className="px-4 py-3 text-slate-400">{row.total_days}</td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${
                            row.attendance_percentage >= 75 ? 'text-emerald-400' :
                            row.attendance_percentage >= 50 ? 'text-amber-400' : 'text-rose-400'
                          }`}>
                            {row.attendance_percentage}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <>
                  {/* Summary Cards */}
                  {reportData.total_paid !== undefined && (
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-slate-950 border border-slate-800 rounded-lg text-center">
                        <p className="text-sm text-slate-400 mb-1">Total Collected</p>
                        <p className="text-2xl font-bold text-emerald-400">KWD {reportData.total_paid}</p>
                      </div>
                      <div className="p-4 bg-slate-950 border border-slate-800 rounded-lg text-center">
                        <p className="text-sm text-slate-400 mb-1">Students Pending</p>
                        <p className="text-2xl font-bold text-amber-400">{reportData.total_pending}</p>
                      </div>
                    </div>
                  )}

                  {/* Pending Fees Table */}
                  {(() => {
                    const pendingStudents = [...reportData.report]
                      .filter(r => r.status === 'Pending')
                      .sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''));
                    const paidStudents = [...reportData.report]
                      .filter(r => r.status === 'Paid')
                      .sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''));

                    return (
                      <div className="space-y-8">
                        <div>
                          <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center gap-2">
                            <Clock size={20} weight="bold" />
                            Pending Fees ({pendingStudents.length} students)
                          </h3>
                          {pendingStudents.length > 0 ? (
                            <table className="w-full" data-testid="pending-fees-table">
                              <thead className="bg-slate-950 border-b border-slate-800">
                                <tr>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">S.No</th>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Student Name</th>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Batch</th>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-800">
                                {pendingStudents.map((row, idx) => (
                                  <tr key={idx} className="hover:bg-slate-800/50">
                                    <td className="px-4 py-3 text-slate-400 font-medium">{idx + 1}</td>
                                    <td className="px-4 py-3 text-slate-50 font-medium">{row.student_name}</td>
                                    <td className="px-4 py-3">
                                      <span className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm font-medium">
                                        {getBatchName(row.batch_id)}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="px-3 py-1 bg-amber-600/20 text-amber-400 rounded-full text-sm font-medium">Pending</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-slate-500 py-4">No pending fees for this month</p>
                          )}
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                            <CheckCircle size={20} weight="bold" />
                            Paid ({paidStudents.length} students)
                          </h3>
                          {paidStudents.length > 0 ? (
                            <table className="w-full" data-testid="paid-fees-table">
                              <thead className="bg-slate-950 border-b border-slate-800">
                                <tr>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">S.No</th>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Student Name</th>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Batch</th>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Amount</th>
                                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Payment Date</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-800">
                                {paidStudents.map((row, idx) => (
                                  <tr key={idx} className="hover:bg-slate-800/50">
                                    <td className="px-4 py-3 text-slate-400 font-medium">{idx + 1}</td>
                                    <td className="px-4 py-3 text-slate-50 font-medium">{row.student_name}</td>
                                    <td className="px-4 py-3">
                                      <span className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm font-medium">
                                        {getBatchName(row.batch_id)}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-emerald-400 font-semibold">KWD {row.amount}</td>
                                    <td className="px-4 py-3 text-slate-400">{row.payment_date ? new Date(row.payment_date).toLocaleDateString() : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-slate-500 py-4">No payments recorded for this month</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">No data available</div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Reports;
